import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { NavItem, TabContent, TabPane } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import useApi from 'hooks/useApi';
import { setSelectedState, setSelectedTag } from 'redux/actions/topBarActions';
import { addBreadcrumb, removeBreadcrumb } from 'redux/actions/layoutActions';
import {
  loadDocument,
  setShowStateFilter,
  setContactsCount,
  setClientsCount,
  setFoldersCount,
  setDocumentsCount,
  setCommunicationsCount,
  setMassiveCommunicationsCount,
  setGeneralSearchText,
} from 'redux/actions/resultActions';

import BackgroundBox from 'shared/components/BackgroundBox';
import { NavHome, NavLink } from 'shared/components/SharedStyles/SharedStyles';
import { colors } from 'shared/defaultTheme';

import { ReactComponent as CloseIcon } from 'assets/icon-close-x.svg';
import { ReactComponent as SearchIcon } from 'assets/icon-search.svg';
import { TextField } from 'shared/components/form/Inputs';
import { L10n, setCulture } from '@syncfusion/ej2-base';
import { ResultsWrapper, CloseButton } from './styles';
import ClientResults from './components/ClientResults';
import DocumentResults from './components/DocumentResults';
import FolderResults from './components/FolderResults';
import ContactResults from './components/ContactResults';
import CommunicationResults from './components/CommunicationResults';
import MassiveCommunicationResults from './components/MassiveCommunicationResults';
import { L10nObj } from './contants';

const ResultsSearchAllSite = () => {
  const { t } = useTranslation();
  const { breadcrumbs } = useSelector((state) => state.layout);
  const { pathname } = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { mainColor } = useSelector((state) => state.theme);
  const { cusNavId, isCustomer, mail, clientId } = useSelector((state) => state.auth);
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);

  const [tagsList, setTagsList] = useState(null);
  const [statesList, setStatesList] = useState(null);
  const [fullStates, setFullStates] = useState(null);
  const [documentTypes, setDocumentTypes] = useState(null);
  const {
    isDocument,
    clientsCount,
    contactsCount,
    foldersCount,
    documentsCount,
    communicationsCount,
    massiveCommunicationsCount,
    generalSearchText,
  } = useSelector((state) => state.results);
  const { customAreaApi } = useApi();
  const [activeTab, setActiveTab] = useState(isCustomer ? '1' : '3');

  const refClients = useRef();
  const refContacts = useRef();
  const refFolders = useRef();
  const refDocuments = useRef();
  const refCommunications = useRef();
  const refMassiveCommunications = useRef();

  useEffect(() => {
    return () => {
      dispatch(setShowStateFilter(false));
      dispatch(setSelectedState([]));
      dispatch(setSelectedTag([]));
    };
  }, []);

  useEffect(() => {
    if (isDocument) {
      setActiveTab('4');
      dispatch(loadDocument(false));
    }
  }, [isDocument]);

  const [, fetchDocumentTypes] = customAreaApi(
    {
      url: `${window.API_V1}/cli/items/getTypeDocuments/${cusNavId}`,
      method: 'GET',
    },
    { manual: true }
  );

  const getDocumentTypes = async () => {
    const {
      data: { data: documentTypesData },
    } = await fetchDocumentTypes();
    const documentTypesArray = documentTypesData.map((type) => ({
      id: type,
      text: type,
    }));
    documentTypesArray.sort((a, b) => a.text.localeCompare(b.text));
    setDocumentTypes(documentTypesArray);
  };

  useEffect(() => {
    getDocumentTypes();
    if (breadcrumbs.length > 1) {
      dispatch(removeBreadcrumb());
      breadcrumbs.forEach(() => {
        dispatch(removeBreadcrumb());
      });
      dispatch(
        addBreadcrumb({
          active: false,
          label: 'general_home',
          link: '/home',
          isModify: false,
        })
      );
    }

    dispatch(
      addBreadcrumb({
        label: 'results_search_all_title',
        active: true,
        link: pathname,
        isModify: true,
      })
    );
    if (isCustomer) {
      dispatch(
        addBreadcrumb({
          label: t('results_search_clients_link'),
          active: true,
          link: null,
          isModify: false,
        })
      );
    } else {
      dispatch(
        addBreadcrumb({
          label: t('general_pagination_folders'),
          active: true,
          link: null,
          isModify: false,
        })
      );
    }
  }, []);

  const getActiveTab = (numberTab, title) => {
    if (numberTab === '4') {
      dispatch(setShowStateFilter(true));
    } else {
      dispatch(setShowStateFilter(false));
      dispatch(setSelectedState([]));
      dispatch(setSelectedTag([]));
    }
    setActiveTab(numberTab);
    dispatch(
      addBreadcrumb({
        label: title,
        active: true,
        link: null,
        isModify: false,
      })
    );
  };

  const onCloseResults = () => {
    history.push('/home');
    if (breadcrumbs.length > 1) {
      breadcrumbs.forEach(() => {
        dispatch(removeBreadcrumb());
      });
    }
  };

  const handleGeneralSearch = (e) => {
    if (e) {
      const { value } = e.target;
      dispatch(setGeneralSearchText(value));
    } else {
      dispatch(setGeneralSearchText(''));
    }
  };

  const [, getClientsCount] = customAreaApi(
    {
      url: `${window.API_V1}/cli/clients/${cusNavId}/search/all/count`,
      method: 'POST',
    },
    { manual: true }
  );

  const [, getContactsCount] = customAreaApi(
    {
      url: `${window.API_V1}/cli/contacts/${cusNavId}/search/all/count`,
      method: 'POST',
    },
    { manual: true }
  );

  const [, getFoldersCount] = customAreaApi(
    {
      url: `${window.API_V1}/cli/items/${cusNavId}/${encodeURIComponent(mail)}/search/all/count`,
      method: 'POST',
    },
    { manual: true }
  );

  const [, getDocumentsCount] = customAreaApi(
    {
      url: `${window.API_V1}/cli/items/${cusNavId}/${encodeURIComponent(mail)}/search/all/count`,
      method: 'POST',
    },
    { manual: true }
  );

  const [, getCommunicationsCount] = customAreaApi(
    {
      url: `${window.API_V1}/cli/notifications/${cusNavId}/search/all/count`,
      method: 'POST',
    },
    { manual: true }
  );

  const [, getMassiveCommunicationsCount] = customAreaApi(
    {
      url: `${window.API_V1}/gen/not/${cusNavId}/search/all/count?login=${mail}`,
      method: 'POST',
    },
    { manual: true }
  );

  const fetchCount = async (body, getter, setter) => {
    const {
      data: { data: counter },
    } = await getter({
      data: body,
    });

    dispatch(setter(counter));
  };

  const getCounters = async () => {
    const body = {
      reduce: {
        limit: 0,
        skip: 0,
      },
      filterText: {
        regexFilter: generalSearchText,
      },
    };

    await Promise.all([
      fetchCount(
        {
          ...body,
          filters: [],
        },
        getClientsCount,
        setClientsCount
      ),
      fetchCount(
        {
          ...body,
          filters: [
            {
              model: 'Contact',
              field: 'Admin',
              filter: [false],
            },
          ],
        },
        getContactsCount,
        setContactsCount
      ),
      fetchCount(
        {
          ...body,
          filters: !isCustomer
            ? [
                {
                  model: 'PathItem',
                  field: 'IsFile',
                  filter: [false],
                },
                {
                  model: 'PathItem',
                  field: 'ClientId',
                  filter: [clientId],
                },
              ]
            : [
                {
                  model: 'PathItem',
                  field: 'IsFile',
                  filter: [false],
                },
              ],
        },
        getFoldersCount,
        setFoldersCount
      ),
      fetchCount(
        {
          ...body,
          filters: !isCustomer
            ? [
                {
                  model: 'PathItem',
                  field: 'IsFile',
                  filter: [true],
                },
                {
                  model: 'PathItem',
                  field: 'ClientId',
                  filter: [clientId],
                },
              ]
            : [
                {
                  model: 'PathItem',
                  field: 'IsFile',
                  filter: [true],
                },
              ],
        },
        getDocumentsCount,
        setDocumentsCount
      ),
      fetchCount(
        {
          ...body,
          filters: !isCustomer
            ? [
                {
                  model: 'Notify',
                  field: 'ClientId',
                  filter: [clientId],
                },
                {
                  model: 'Notify',
                  field: 'Mail',
                  filter: [mail],
                },
              ]
            : [],
        },
        getCommunicationsCount,
        setCommunicationsCount
      ),
      fetchCount(
        {
          ...body,
          filters: !isCustomer
            ? [
                {
                  model: 'GeneralNotify',
                  field: 'Created',
                  asc: false,
                },
                {
                  model: 'GeneralNotify',
                  field: 'Name',
                  filter: null,
                  isRegex: false,
                },
                {
                  model: 'GeneralNotify',
                  field: 'ClientId',
                  filter: [clientId],
                },
              ]
            : [
                {
                  model: 'GeneralNotify',
                  field: 'Created',
                  asc: false,
                },
                {
                  model: 'GeneralNotify',
                  field: 'Name',
                  filter: null,
                  isRegex: false,
                },
              ],
        },
        getMassiveCommunicationsCount,
        setMassiveCommunicationsCount
      ),
    ]);
  };

  useEffect(() => {
    (async () => {
      await getCounters();
    })();
  }, [activeTab]);

  const [, getTags] = customAreaApi(
    {
      url: `${window.API_V1}/cus/tags/${cusNavId}/search`,
      method: 'POST',
      data: {
        filters: [
          {
            model: 'Tag',
            field: 'Name',
            asc: false,
          },
        ],
        reduce: {
          limit: 0,
          skip: 0,
        },
      },
    },
    { manual: true }
  );

  const [, getStates] = customAreaApi(
    {
      url: `${window.API_V1}/cus/StatesClients/${cusNavId}`,
      method: 'GET',
      data: {
        filters: [
          {
            model: 'State',
            field: 'Name',
            asc: false,
          },
        ],
        reduce: {
          limit: 0,
          skip: 0,
        },
      },
    },
    { manual: true }
  );

  const getStatesData = async () => {
    const {
      data: { data: states },
    } = await getStates();
    const stateNames =
      states.map((x) => {
        if (!x.i18List) return null;
        const state = x.i18List.find((y) => y.i18Code === selectedDefaultLanguage);
        return {
          text: state?.i18Name || x.i18List[0]?.i18Name || '',
          id: x.id,
        };
      }) || [];
    setFullStates(states);
    stateNames.sort((a, b) => a.text.localeCompare(b.text));
    setStatesList(stateNames);
  };

  const getTagsData = async () => {
    const {
      data: { data: tags },
    } = await getTags();

    const tagNames =
      tags
        .map((x) => {
          if (!x.i18List) return null;
          const tag = x.i18List.find((y) => y.i18Code === selectedDefaultLanguage);
          return {
            text: tag?.i18Name || x.i18List[0]?.i18Name || '',
            id: x.id,
          };
        })
        .filter((el) => el !== null) || [];
    tagNames.sort((a, b) => a.text.localeCompare(b.text));
    setTagsList(tagNames);
  };

  useEffect(() => {
    getStatesData();
    getTagsData();
    return () => {
      setStatesList(null);
      setTagsList(null);
      setFullStates(null);
    };
  }, []);

  useEffect(() => {
    getTagsData();
    const lang = selectedDefaultLanguage.slice(0, 2);
    setCulture(lang);
    L10n.load(L10nObj);
  }, [selectedDefaultLanguage]);

  const onSearch = async () => {
    if (refClients?.current) await refClients.current.doSearch();
    if (refContacts?.current) await refContacts.current.doSearch();
    if (refFolders?.current) await refFolders.current.doSearch();
    if (refDocuments?.current) await refDocuments.current.doSearch();
    if (refCommunications?.current) await refCommunications.current.doSearch();
    if (refMassiveCommunications?.current) await refMassiveCommunications.current.doSearch();
  };

  const onInputChange = async (e) => {
    if (e.key === 'Enter') {
      await onSearch();
    }
  };

  const onButtonClearClick = async () => {
    await dispatch(setGeneralSearchText(''));
    await onSearch();
  };

  return (
    <>
      <CloseButton>
        <button type="button" className="close-button" aria-label="Close" onClick={onCloseResults}>
          <span aria-hidden="true">{t('results_search_all_close')}</span>
          <CloseIcon />
        </button>
      </CloseButton>
      <ResultsWrapper>
        <BackgroundBox className="backgroundbox-wrapper">
          <>
            <div className="form flex-column general-search-input" noValidate>
              <div className="d-flex flex-row align-items-center h-50 ml-filter-section selector-filter-by-wrapper" />
              <TextField
                borderRadius={2}
                className="mb-4 m-0 h-50 filter-input"
                inputClassName="w-100"
                withButtonClear
                onButtonClearClick={onButtonClearClick}
                rightIcon={
                  <SearchIcon
                    fill={colors.black}
                    onClick={onSearch}
                    style={{ cursor: 'pointer' }}
                  />
                }
                placeholder={t('results_search_placeholder_all')}
                value={generalSearchText ?? ''}
                onChange={handleGeneralSearch}
                onKeyDown={onInputChange}
              />
            </div>

            <NavHome tabs>
              {isCustomer && (
                <>
                  <NavItem>
                    <NavLink
                      color={activeTab === '1' ? mainColor : colors.pattens}
                      selected={activeTab === '1'}
                      onClick={() => getActiveTab('1', 'results_search_clients_link')}
                    >
                      <div>{t('results_search_clients_link')}</div>
                      <div>{`(${clientsCount})`}</div>
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      color={activeTab === '2' ? mainColor : colors.pattens}
                      selected={activeTab === '2'}
                      onClick={() => getActiveTab('2', 'results_search_contacts_link')}
                    >
                      <div>{t('results_search_contacts_link')}</div>
                      {`(${contactsCount})`}
                    </NavLink>
                  </NavItem>
                </>
              )}
              <NavItem>
                <NavLink
                  color={activeTab === '3' ? mainColor : colors.pattens}
                  selected={activeTab === '3'}
                  onClick={() => getActiveTab('3', 'general_pagination_folders')}
                >
                  <div>{t('general_pagination_folders')}</div>
                  <div>{`(${foldersCount})`}</div>
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  color={activeTab === '4' ? mainColor : colors.pattens}
                  selected={activeTab === '4'}
                  onClick={() => getActiveTab('4', 'general_pagination_documents')}
                >
                  <div>{t('general_pagination_documents')}</div>
                  <div>{`(${documentsCount})`}</div>
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  color={activeTab === '5' ? mainColor : colors.pattens}
                  selected={activeTab === '5'}
                  onClick={() => getActiveTab('5', 'results_search_notifications_link')}
                >
                  <div>{t('results_search_notifications_link')}</div>
                  {`${
                    isCustomer
                      ? t('notifications_text_individual')
                      : t('notifications_text_personals')
                  } (${communicationsCount})`}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  color={activeTab === '6' ? mainColor : colors.pattens}
                  selected={activeTab === '6'}
                  onClick={() => getActiveTab('6', 'results_search_notifications_link')}
                >
                  <div>{t('results_search_notifications_link')}</div>
                  {`${
                    isCustomer ? t('notifications_text_massive') : t('notifications_text_diffusion')
                  } (${massiveCommunicationsCount})`}
                </NavLink>
              </NavItem>
            </NavHome>
            <TabContent activeTab={activeTab}>
              <TabPane tabId="1">
                {activeTab === '1' && !!tagsList && !!statesList && !!fullStates && (
                  <ClientResults
                    statesList={statesList}
                    tagsList={tagsList}
                    fullStates={fullStates}
                    getCounters={getCounters}
                    ref={refClients}
                  />
                )}
              </TabPane>
              <TabPane tabId="2">
                {activeTab === '2' && (
                  <ContactResults getCounters={getCounters} ref={refContacts} />
                )}
              </TabPane>
              <TabPane tabId="3">
                {activeTab === '3' && <FolderResults getCounters={getCounters} ref={refFolders} />}
              </TabPane>
              <TabPane tabId="4">
                {activeTab === '4' && !!documentTypes && (
                  <DocumentResults
                    documentTypes={documentTypes}
                    getCounters={getCounters}
                    ref={refDocuments}
                  />
                )}
              </TabPane>
              <TabPane tabId="5">
                {activeTab === '5' && (
                  <CommunicationResults getCounters={getCounters} ref={refCommunications} />
                )}
              </TabPane>
              <TabPane tabId="6">
                {activeTab === '6' && (
                  <MassiveCommunicationResults
                    getCounters={getCounters}
                    ref={refMassiveCommunications}
                  />
                )}
              </TabPane>
            </TabContent>
          </>
        </BackgroundBox>
      </ResultsWrapper>
    </>
  );
};

export default ResultsSearchAllSite;
